import * as uuid from "uuid";

import { EditGridColumn, NewGridColumn } from "./components";
import { GridFieldType, IGridColumn } from "../../../base/models";
import React, { useEffect, useState } from "react";

import { IDataSource } from "../../../base/types";
import { MUIIcon } from "../../../widgets";
import Modal from "react-bootstrap/esm/Modal";
import clsx from "clsx";

export const EditGridColumns = ({ columns, dataSource, className, onChange }: {
    dataSource?: IDataSource,
    columns: IGridColumn[],
    className?: string,
    onChange: (columns: IGridColumn[]) => void,
}) => {
    type GridColumnWithKey = IGridColumn & { key: string }

    const [gridColumns, setGridColumns] = useState<GridColumnWithKey[]>([])
    const [editColumn, setEditColumn] = useState<GridColumnWithKey>();
    const [showAddColumn, setShowAddColumn] = useState<boolean>(false)

    useEffect(() => {
        const gridColumns = columns.map<GridColumnWithKey>((column, index) => ({
            key: uuid.v1(),
            ...column
        }))

        setGridColumns(gridColumns);
    }, [columns])

    const handleOnAdd = (column: IGridColumn) => {
        onChange([...columns, column]);
        setEditColumn(undefined)
    }

    const handleOnUpdate = (key: string, column: IGridColumn) => {
        onChange(gridColumns.map(p => p.key === key ? column : p))
        setEditColumn(undefined)
    }

    return <div className={clsx("table-responsive", className)}>
        <table className="table table-sm table-row-dashed table-row-gray-300 border gx-5">
            <thead>
                <tr className="bg-light">
                    <th>
                        <div className="d-flex align-items-center gx-5">
                            <h6 className={clsx("my-0 flex-fill")}>
                                <MUIIcon iconName={"ViewColumnOutlined"} className="me-1" />
                                Grid Columns
                            </h6>

                            <button
                                className="btn btn-sm py-2 btn-success"
                                type="button"
                                onClick={() => setShowAddColumn(true)}>
                                <MUIIcon iconName={"AddOutlined"} className="me-1" />
                                Add column
                            </button>
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody>
                {[...gridColumns]
                    .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                    .map((column, index) => <React.Fragment key={index} >
                        <tr>
                            <td>
                                <div className="d-flex align-items-center">
                                    <div className="w-25px h-25px rounded-circle bg-dark me-2 d-flex align-items-center justify-content-center">
                                        <span className="text-inverse-dark fw-semibold fs-9">{column.sequence || 0}</span>
                                    </div>
                                    <div className="flex-fill">
                                        <label className="fw-semibold mb-0">
                                            <span className="me-1">
                                                {column.header || ((column as any).label)}
                                            </span>
                                            <span className="badge badge-success p-1 fs-8">{column.fieldType || GridFieldType.Text}</span>
                                        </label>
                                    </div>
                                    <div className="d-flex gap-3 align-items-center">
                                        <button
                                            type="button"
                                            onClick={() => setEditColumn(column)}
                                            className="btn btn-sm btn-warning py-1">
                                            <MUIIcon iconName={"EditOutlined"} className="fs-6 me-1" />
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onChange(gridColumns.filter((gridColumn) => gridColumn.key !== column.key))}
                                            className="btn btn-sm btn-danger py-1">
                                            <MUIIcon iconName={"DeleteOutlined"} className="fs-6 me-1" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </React.Fragment>)}
            </tbody>
        </table>

        <div>
            {(!!editColumn) && <>
                <EditGridColumnDialog
                    show={true}
                    column={editColumn}
                    dataSource={dataSource}
                    onUpdateColumn={(column) => handleOnUpdate(editColumn.key, column)}
                    onHideModal={() => setEditColumn(undefined)}
                />
            </>}

            <NewGridColumnDialog
                show={showAddColumn}
                dataSource={dataSource}
                onAddColumn={handleOnAdd}
                onHideModal={() => setShowAddColumn(false)}
            />
        </div>
    </div>
}

const EditGridColumnDialog = ({ dataSource, show, column, onUpdateColumn, onHideModal }: {
    show: boolean,
    column: IGridColumn,
    dataSource?: IDataSource,
    onUpdateColumn: (column: IGridColumn) => void,
    onHideModal: () => void,
}) => {
    const handleOnChange = (column: IGridColumn) => {
        onUpdateColumn(column);
        onHideModal();
    }

    return (
        <Modal show={show} onHide={onHideModal} dialogClassName="min-w-75">
            <Modal.Header closeButton className="py-5">
                <Modal.Title>Edit column</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <EditGridColumn
                    dataSource={dataSource}
                    column={column}
                    onCancel={() => onHideModal()}
                    onChange={handleOnChange}
                />
            </Modal.Body>
        </Modal>)
}

const NewGridColumnDialog = ({ dataSource, show, onAddColumn, onHideModal }: {
    show: boolean,
    dataSource?: IDataSource,
    onAddColumn: (column: IGridColumn) => void,
    onHideModal: () => void,
}) => {
    const handleOnAdd = (column: IGridColumn) => {
        onAddColumn(column);
        onHideModal();
    }

    return (
        <Modal show={show} onHide={onHideModal} dialogClassName="min-w-75">
            <Modal.Header closeButton className="py-5">
                <Modal.Title>Add new column</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <NewGridColumn
                    dataSource={dataSource}
                    onAdd={handleOnAdd}
                    onCancel={onHideModal} />
            </Modal.Body>
        </Modal>)
}